import { useTranslation } from 'react-i18next';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Building2, Save, Upload } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { PhoneInput } from '@/components/ui/phone-input';
import { useToast } from '@/hooks/use-toast';
import { organizationService } from '@/services/api/organization.service';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sanitizeErrorMessage } from '@/lib/error-utils';

const organizationSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  description: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  phone: z.string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => !val || val === '' || isValidPhoneNumber(val),
      'Please enter a valid phone number for the selected country'
    ),
  address: z.string().optional(),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  logo_url: z.string().url('Invalid URL').optional().or(z.literal('')),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

export default function OrganizationPage() {
  const { t } = useTranslation(['settings', 'common']);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useSetPageHeader(
    {
      title: t('settings:profile.title'),
      sub: t('settings:profile.subtitle'),
    },
    [t],
  );

  const { data: organization, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.get(),
  });

  const updateMutation = useMutation({
    mutationFn: (data: OrganizationFormData) => organizationService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast({
        title: t('common:success'),
        description: t('settings:toasts.profileSuccess'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: sanitizeErrorMessage(error, t('settings:toasts.profileError')),
        variant: 'destructive',
      });
    },
  });

  // Normalize phone to E.164 format
  const normalizePhone = (phone: string | undefined) => {
    if (!phone) return '';
    if (phone.startsWith('+')) return phone;
    // Assume US number if no country code
    return '+1' + phone.replace(/\D/g, '');
  };

  const form = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    values: organization
      ? {
          name: organization.name || '',
          description: organization.description || '',
          email: organization.email || '',
          phone: normalizePhone(organization.phone),
          address: organization.address || '',
          website: organization.website || '',
          logo_url: organization.logo_url || '',
        }
      : undefined,
  });

  const onSubmit = (data: OrganizationFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-soft">{t('common:loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings:profile.cardTitle')}</CardTitle>
          <CardDescription>
            Update your organization's basic information that will appear on your public booking page
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('settings:profile.form.namePlaceholder')}
                        className="h-12 text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings:profile.form.description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder={t('settings:profile.form.descriptionPlaceholder')}
                        rows={4}
                        className="text-base resize-none"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('settings:profile.form.email')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-soft" />
                          <Input
                            {...field}
                            type="email"
                            placeholder="contact@example.com"
                            className="h-12 pl-10 text-base"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('settings:profile.form.phone')}</FormLabel>
                      <FormControl>
                        <PhoneInput
                          {...field}
                          placeholder="+1 (555) 123-4567"
                          className="h-12 text-base"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings:profile.form.address')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="123 Main St, City, State 12345"
                        className="h-12 text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings:profile.form.website')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="url"
                        placeholder="https://example.com"
                        className="h-12 text-base"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="logo_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('settings:profile.form.logoUrl')}</FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <Input
                          {...field}
                          type="url"
                          placeholder="https://example.com/logo.png"
                          className="h-12 text-base"
                        />
                        {field.value && (
                          <div className="p-4 border rounded-lg bg-surface-2">
                            <img
                              src={field.value}
                              alt={t('settings:profile.form.logoAlt')}
                              className="max-h-24 object-contain"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="min-h-[48px] px-8 text-base"
                >
                  {updateMutation.isPending ? (
                    <>
                      <Upload className="w-5 h-5 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Save Profile
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
